import db_lib from 'xAppLib/libs/db_lib';
import {ERX_ENUM, CLINICIAN_ENUM} from './erx/resources/erx_enums';
import { isFuture } from 'date-fns';

const _FRDB_LOC = 'docs';

const _DOC_LEVELS = {
    "": 'Clear',
    level1: 'Level 1',
    level2: 'Level 2',
    level3: 'Level 3',
    level4: 'Level 4',
}

const _DOC_CAPABILITIES = {
	cosm: 'Instant Cosmetics',
    advisor: 'Advisor Role',
    surge: 'Surge Doctor',
    fullTimer: 'Full Timer (salaried, not paid by DocPay)',
	weightLoss: 'Weight Management Consultations',
	weightProgram: 'Weight Management Programs',
	carezen: 'CareZen Video Consultations',
	menopause: 'Menopause (upcoming)',
	mentalHealth: 'Mental Health (upcoming)',
	review: 'Review consults (upcoming)',
	general: 'General telehealth (upcoming)',
	express: 'Express services (upcoming)',
	fertility: 'Fertility (upcoming)',
}

const _DET_FIELDS = {
	'firstname':		'First Name',
	'familyname':		'Last Name',
	'name':			 	'Doctor Name',
	'email':			'Contact email',
	'phone':			'Phone number',
	'pub_phone':		'(Optional) Phone number to use on scripts',
	'fax':				'Fax number',
	'skype':			'Skype ID',
	'addr_str':			'Street address',
	'addr_sub':			'Suburb',
	'practicename':		'Practice Name',
	'address1':			'Practice Address line 1',
	'address2':			'Practice Address line 2',
	'pass_to':			'Pass To Dr',
	'suburb':			'Practice Suburb',
	'postcode':			'Practice Postcode',
	'state':			'Practice State',
	'practiceemail':	'Practice Email',
	'provider':			'Provider number',
	'prescriber':		'Prescriber number',
	'prescribertype':	'Prescriber Type',
	'hpio':			 	'HPIO',
	'hpii':				'HPII',
	'ahpra':			'AHPRA Number',
	'qualification':	'Qualification',
	'erx_id':			'eRx identity number',
	'minorID':			'Medicare Location ID',
	'signature':		'Signature file',
	'doc_level':		'Current Level',
	'capabilities': 	'Capabilities',
	'notes':			'Notes',
	'moratorium_date':			'Moratorium date',
}

const _SIGNUP_FIELDS = {
	heidi: {
		label:'Enable HeidiAI',
		valid_not_required: true
	},
	mims: {
		label:'Enroll in MIMS',
		valid_not_required: true
	},
	group: {
		label:'Group',
		placeholder: 'Select Group',
		valid_not_required: true
	},
	organisation: {
		label:'Organisation',
		readOnly: true,
		valid_not_required: true
	},
	firstname: {
		label:'First Name',
		placeholder: 'First Name',
	},
	familyname: {
		label:'Last Name',
		placeholder: 'Last Name',
	},
	name: {
		label:'Doctor Name',
		placeholder: 'Doctor Name',
	},
	email: {
		label:'Email',
		type:'email',
		placeholder:'Preferred Email'
	},
	phone: {
		label:'Phone Number',
		placeholder: '04XX XXX XXX',
		type: 'masked',
		mask: [/\d/, /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/],
		val_proc: v => v.replace(/\s/g, '').replace(/^\+610?/, '0').replace(/^\+61/, '0'),
		pattern: /^04\d{8}$/,
		errors: () => 'Phone must be a valid Australian mobile number (04XX XXX XXX)',
	},
	pub_phone: {
		label:'Optional To Use On Scripts',
		placeholder:'Public Number',
		valid_not_required: true
	},
	fax: {
		label:'Fax Number',
		placeholder:'Fax Number',
		valid_not_required: true
	},
	skype: {
		label:'Skype ID',
		placeholder:'Skype ID',
		valid_not_required: true
	},
	// Legacy duplicates
	addr_str: {
		label:'Street Address',
		placeholder:'Street Address',
		valid_not_required: true
	},
	addr_sub: {
		label:'Suburb',
		placeholder:'Suburb',
		valid_not_required: true
	},
	// /
	practicename: {
		label:'Practice Name',
		placeholder:'Practice Name',
		readOnly: true
	},
	address1: {
		label:'Practice Address line 1',
		placeholder:'Practice Address line 1',
		readOnly: true
	},
	address2: {
		label:'Practice Address line 2',
		placeholder:'Practice Address line 2',
		readOnly: true,
		valid_not_required: true
	},
	suburb: {
		label:'Suburb',
		placeholder:'Practice Suburb',
		readOnly: true
	},
	postcode: {
		label:'Postcode',
		placeholder:'Practice Postcode',
		readOnly: true
	},
	state: {
		label:'State',
		placeholder:'Practice State',
		type: 'select',
		options: Object.entries(ERX_ENUM.STATE).map(([text, value]) => ({ text, value, key: value }))
	},
	practiceemail: {
		label:'Email',
		type:'email',
		placeholder:'Practice Email',
	},
	provider: {
		label:'Provider Number',
		placeholder:'Provider Number',
	},
	prescriber: {
		label:'Prescriber Number',
		placeholder:'Prescriber Number',
		pattern: /^\d{1,10}$/,
		errors: () => 'Prescriber number must no more than 10 digits',
	},
	prescribertype: {
		label: 'Prescriber Type',
		type: 'select',
		options: Object.entries(CLINICIAN_ENUM.PRESCRIBER_TYPE).map(
			([text, value]) => ({
				// Format the options enum text e.g.
				// "Medical Practitioner" -> "Medical Practitioner"
				// "Eye/Optometrist": "Eye / Optometrist"
				// "nUrse": "Nurse"
				// "midwiFe": "Midwife"
				text:
					text
						.replace(/\//g, " / ")
						.toLowerCase()
						.split(" ")
						.map(word => word.slice(0, 1).toUpperCase() + word.slice(1))
						.join(" "),
				value,
				key: value
			})
		),
	},
	hpio: {
		label:'HPIO',
		placeholder: '800362XXXXXXXXXX',
		type: 'masked',
		mask: new Array(16).fill(/\d/),
		pattern: /^800362\d{10}$/,
		errors: () => 'HPIO must be 16 digits starting with 800362',
	},
	hpii: {
		label:'HPII',
		placeholder: '800361XXXXXXXXXX',
		type: 'masked',
		mask: new Array(16).fill(/\d/),
		pattern: /^800361\d{10}$/,
		errors: () => 'HPII must be 16 digits starting with 800361',
	},
	ahpra: {
		label:'AHPRA Number',
		placeholder: 'AHPRA Number',
		type: 'masked',
		mask: [...new Array(3).fill(/[A-Za-z]/), ...new Array(10).fill(/\d/)],
		val_proc: v => v.toUpperCase(),
		pattern: /^[A-Z]{3}\d{10}$/,
		errors: () => 'AHPRA number must be 3 uppercase letters followed by 10 digits',
	},
	qualification: {
		label:'Qualification',
		placeholder: 'Qualification',
		validate_function: v => !v || v.trim().length <= 50,
		errors: () => 'Qualification must be 50 characters or fewer',
	},
	has_no_moratorium: {
		label: `I'm NOT under a moratorium`,
		type: 'checkbox',
		value: true,
		valid_not_required: true,
	},
	moratorium_date: {
		label: 'When does your moratorium end?',
		type: 'date',
		value: null,
		valid_not_required: (values) => values.has_no_moratorium,
		validate_function: (v, values) => values.has_no_moratorium || v && isFuture(new Date(v)),
		errors: () => 'Moratorium end date must be in the future',
	},
	erx_id: {
		label:'eRx identity number',
		placeholder:'eRx identity number',
		valid_not_required: true,
	},
	signature: {
		label:'Signature File',
		placeholder:'Signature file',
		get readOnly() {
			return !app.acl.can_onboard_doc;
		},
		get valid_not_required() {
			return app.acl.can_onboard_doc;
		}
	},
	doc_level: {
		label:'Current Level',
		placeholder:'Select Doctor Level',
		type: 'select',
		options: Object.entries(_DOC_LEVELS).map(([value, text]) => ({ text, value, key: value }))
	},
	capabilities: {
		label: 'Capabilities',
		placeholder: 'Capabilities',
		valid_not_required: true
	},
	notes: {
		label:'Notes',
		placeholder:'Notes',
		valid_not_required: true
	},
	date_created: {
		readOnly: true
	},
	recap: {
		name: 'recaptcha',
		get valid_not_required() {
			return app.acl.is_admin;
		}
	}
}


const _MODEL_NAME = 'doc_model';

// ---- --------------------------------------------  --------------------------------------------  
// ---- --------------------------------------------  --------------------------------------------  

export default class doc_model extends db_lib {
	static get FRDB_LOC() { return _FRDB_LOC; }
	static get DET_FIELDS() { return _DET_FIELDS; }
	static get DOC_LEVELS() { return _DOC_LEVELS; }
	static get DOC_CAPABILITIES() { return _DOC_CAPABILITIES; }
	static get SIGNUP_FIELDS() { return _SIGNUP_FIELDS; }
	static get MODEL_NAME() { return _MODEL_NAME; }

	// ----	--------------------------------------------	--------------------------------------------
	// ----	--------------------------------------------	--------------------------------------------


	static providerNumberTransformer(v){
		return v?.toUpperCase().replace(/[^0-9A-Z]/g, '');
	}
}

			// .text(params.alt_cat!="_cosm" ? 'Dr. Asher Freilich' :			 'Dr. Greg Scher', 55, 18)

			// .text(params.alt_cat!="_cosm" ? '50 Orrong Crescent' :			 '2A Mawby Rd', 55, 37)

			// .text(params.alt_cat!="_cosm" ? 'Caulfield North - VIC 3161' :			 'Bentleigh East', 55, 56)

			// .text('Prescriber number:			 ' + (params.alt_cat!="_cosm" ? '2596769' :			 '2368034'), 55, 117)

			// .text('Provider number:			 ' + (params.alt_cat!="_cosm" ? '4449747J' :			 '289833UW'), 441, 117)

			// .image(params.alt_cat!="_cosm" ? './imgs/sign - asher.png' :			 './imgs/sign - greg.png', 275, 725, {width:			 189})
